<template lang="pug">
div.record-review-overlay(@keydown.esc.stop="close" tabindex="-1" ref="overlay")
  v-card.record-review-page(tile)
    .record-review-header
      .record-review-header-left
        .rrl-title-bar(v-if="activeField")
          button.rrl-back-btn(@click="close" aria-label="go back")
            img(:src="goBackIcon" width="20" height="20" alt="back")
          .rrl-separator
          img.rrl-file-type-icon(:src="currentFileTypeIcon" width="20" height="20" alt="file type")
          .rrl-name-nav-group
            span.rrl-filename(:title="currentFileName") {{ currentFileName }}
            .rrl-field-nav
              button.rr-nav-btn(:disabled="fileColumnsWithFile.length <= 1" @click="goPrevField()" aria-label="previous field")
                svg(width="20" height="20" viewBox="0 0 20 20" fill="none")
                  path(fill-rule="evenodd" clip-rule="evenodd" d="M6.175 12.842L5 11.667L10 6.66699L15 11.667L13.825 12.842L10 9.02533L6.175 12.842Z" fill="currentColor")
              button.rr-nav-btn(:disabled="fileColumnsWithFile.length <= 1" @click="goNextField()" aria-label="next field")
                svg(width="20" height="20" viewBox="0 0 20 20" fill="none")
                  path(fill-rule="evenodd" clip-rule="evenodd" d="M6.175 6.325L5 7.5L10 12.5L15 7.5L13.825 6.325L10 10.1417L6.175 6.325Z" fill="currentColor")
          v-btn.ref-action-btn(v-if="currentFileUrl" text :ripple="false" min-width="0" height="40" @click="openCurrentFile")
            v-icon.ref-action-btn__icon.mr-1(size="20") mdi-open-in-new
            span.ref-action-btn__label Open
      .record-review-header-right
        .rr-left-group
          .rr-breadcrumb-area
            v-breadcrumbs.pa-0(:items="breadcrumbItems" divider="/")
              template(v-slot:item="{ item, index }")
                v-breadcrumbs-item(
                  :disabled="item.disabled"
                  @click.prevent="onBreadcrumbClick(item)"
                  :class="{ 'rr-bc-active': index === breadcrumbItems.length - 1, 'rr-bc-normal': index !== breadcrumbItems.length - 1 }"
                ) {{ item.text }}
          .rr-nav-frame
            button.rr-nav-btn(:disabled="isFirst" @click="navigatePrev()" aria-label="previous record")
              svg(width="20" height="20" viewBox="0 0 20 20" fill="none")
                path(fill-rule="evenodd" clip-rule="evenodd" d="M6.175 12.842L5 11.667L10 6.66699L15 11.667L13.825 12.842L10 9.02533L6.175 12.842Z" fill="currentColor")
            button.rr-nav-btn(:disabled="isLast" @click="navigateNext()" aria-label="next record")
              svg(width="20" height="20" viewBox="0 0 20 20" fill="none")
                path(fill-rule="evenodd" clip-rule="evenodd" d="M6.175 6.325L5 7.5L10 12.5L15 7.5L13.825 6.325L10 10.1417L6.175 6.325Z" fill="currentColor")
        v-btn.record-review-close-btn(icon @click="close" aria-label="close")
          v-icon mdi-close
    .record-review-body
      v-row.no-gutters.fill-height
        v-col.record-review-left-pane(cols="6")
          slot(name="document-preview")
            template(v-if="activeField && currentFileData")
              document-preview-pane(
                ref="documentPreviewPane"
                :column_name="activeField"
                :rowObjectId="rowData && rowData.object_id ? String(rowData.object_id) : ''"
                :fileUrl="currentFileUrl"
                :fileExtension="currentFileExtension"
                :columnApiName="currentFieldApiName"
                @request-switch-column="onDocumentPreviewRequestSwitchColumn"
              )
            div.da-no-file-msg(v-else-if="isRecordDeleted || noPreviewableFile") {{ $t('DOCUMENT_AGENT.NO_PREVIEWABLE_FILE') }}
            div.da-no-file-msg(v-else-if="noFileInCurrentColumn") {{ $t('DOCUMENT_AGENT.NO_FILE_IN_COLUMN') }}
        v-col.record-review-right-pane(cols="6")
          slot(name="field-data")
            div.da-no-file-msg(v-if="isRecordDeleted") {{ $t('DOCUMENT_AGENT.RECORD_DELETED') }}
</template>

<script>
import Vue from 'vue';
import DocumentPreviewPane from '@/components/DocumentAgent/DocumentPreviewPane.vue';
import UpGPTPortalDataServices from '@/services/UpGPTPortalDataServices';

export default {
  name: 'RecordReviewPage',

  components: {
    DocumentPreviewPane,
  },

  provide() {
    return {
      sasUrlCache: this.sasUrlCache,
      fetchAndCacheSasUrl: this.fetchAndCacheSasUrl,
    };
  },

  props: {
    value: { type: Boolean, required: true },
    agentId: { type: [String, Number], default: null },
    recordId: { type: [String, Number], default: null },
    initialField: { type: String, default: null },
    records: { type: Array, default: () => [] },
    breadcrumbItems: { type: Array, default: () => [] },
    columns: { type: Array, default: () => [] },
    rowData: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      activeField: null,
      // SAS URL 快取，key = `${object_id}:${column_name}`，頁面生命週期內有效
      sasUrlCache: Vue.observable({}),
      goBackIcon: require('@/assets/images/Go_Back.svg'),
      arrowUpIcon: require('@/assets/images/Arrow_Line_Up.svg'),
      arrowDownIcon: require('@/assets/images/Arrow_Line_Down.svg'),
      filePdfIcon: require('@/assets/images/File-pdf.svg'),
      fileDocIcon: require('@/assets/images/File-doc.svg'),
      fileJpgIcon: require('@/assets/images/File-jpg.svg'),
      filePngIcon: require('@/assets/images/File-png.svg'),
      filePptIcon: require('@/assets/images/File-ppt.svg'),
      fileCsvIcon: require('@/assets/images/File-csv.svg'),
      fileTxtIcon: require('@/assets/images/File-txt.svg'),
      fileUnknownIcon: require('@/assets/images/File-unknow-file.svg'),
    };
  },

  created() {
    this.activeField = this.initialField;
  },

  mounted() {
    this.$nextTick(() => this.$refs.overlay?.focus());
  },

  beforeDestroy() {
    // 頁面關閉時清空 SAS URL 快取，不跨頁面生命週期保留
    Object.keys(this.sasUrlCache).forEach(key => {
      Vue.delete(this.sasUrlCache, key);
    });
  },

  watch: {
    initialField(v) {
      this.activeField = v;
    },
  },

  computed: {
    currentIndex() {
      if (this.recordId == null || !this.records.length) return -1;
      return this.records.findIndex(r => String(r.object_id) === String(this.recordId));
    },
    isFirst() {
      return this.currentIndex <= 0;
    },
    isLast() {
      return this.currentIndex === -1 || this.currentIndex >= this.records.length - 1;
    },
    isRecordDeleted() {
      return this.recordId != null && this.records.length > 0 && this.currentIndex === -1;
    },
    isAtRootLevel() {
      return this.breadcrumbItems.length <= 1;
    },
    hasChildCollection() {
      return (this.columns || []).some(
        col => String(col?.flexfield_datatype || '').trim().toLowerCase() === 'collection'
      );
    },
    currentFieldMeta() {
      return (this.columns || []).find(c => c.flexfield_column === this.activeField) || null;
    },
    currentFieldDescription() {
      return this.currentFieldMeta?.description || '';
    },
    currentFieldApiName() {
      return this.currentFieldMeta?.name || '';
    },
    currentFileData() {
      return this.normalizeMaybeJson(this.rowData?.[this.activeField]);
    },
    noPreviewableFile() {
      return this.fileColumnsWithFile.length === 0;
    },
    noFileInCurrentColumn() {
      return !!this.activeField && !this.currentFileData;
    },
    currentFileName() {
      return this.currentFileData?.original_filename || this.currentFileData?.file_name || '';
    },
    currentFileUrl() {
      return this.currentFileData?.original_url || this.currentFileData?.file_url || '';
    },
    currentFileExtension() {
      const name = this.currentFileName || '';
      const parts = name.split('.');
      return parts.length > 1 ? parts.pop().toLowerCase() : '';
    },
    currentFileTypeIcon() {
      const name = this.currentFileName || '';
      const ext = name.split('.').pop().toLowerCase();
      const map = {
        pdf: this.filePdfIcon,
        doc: this.fileDocIcon,
        docx: this.fileDocIcon,
        jpg: this.fileJpgIcon,
        jpeg: this.fileJpgIcon,
        png: this.filePngIcon,
        ppt: this.filePptIcon,
        pptx: this.filePptIcon,
        csv: this.fileCsvIcon,
        txt: this.fileTxtIcon,
      };
      return map[ext] || this.fileUnknownIcon;
    },
    fileColumnsWithFile() {
      return (this.columns || [])
        .filter(col => {
          if (String(col?.flexfield_datatype || '').trim().toLowerCase() !== 'file') return false;
          const raw = col?.flexfield_datadetail;
          const type = (raw !== null && typeof raw === 'object') ? (raw.type ?? null) : (typeof raw === 'string' ? raw : null);
          if (type === 'DocumentOutput') return false;
          const cellValue = this.rowData?.[col.flexfield_column];
          return cellValue !== null && cellValue !== undefined && typeof cellValue === 'object';
        })
        .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    },
  },

  methods: {
    goPrevField() {
      const list = this.fileColumnsWithFile;
      if (list.length <= 1) return;
      const idx = list.findIndex(c => c.flexfield_column === this.activeField);
      const prevIdx = idx <= 0 ? list.length - 1 : idx - 1;
      this.activeField = list[prevIdx].flexfield_column;
    },
    goNextField() {
      const list = this.fileColumnsWithFile;
      if (list.length <= 1) return;
      const idx = list.findIndex(c => c.flexfield_column === this.activeField);
      const nextIdx = idx >= list.length - 1 ? 0 : idx + 1;
      this.activeField = list[nextIdx].flexfield_column;
    },
    close() { this.$emit('input', false) },
    navigatePrev() {
      if (this.isFirst) return;
      this.$emit('navigate', this.records[this.currentIndex - 1]);
    },
    navigateNext() {
      if (this.isLast) return;
      this.$emit('navigate', this.records[this.currentIndex + 1]);
    },
    onBreadcrumbClick(item) {
      this.$emit('breadcrumb-click', item);
    },
    tableUp() {
      if (this.isAtRootLevel) return;
      const prevItem = this.breadcrumbItems[this.breadcrumbItems.length - 2];
      if (!prevItem) return;
      this.$emit('breadcrumb-click', prevItem);
    },
    tableDown() {
      if (!this.hasChildCollection) return;
      this.$emit('table-down');
    },
    normalizeMaybeJson(v) {
      if (!v) return null;
      if (typeof v === 'object') return v;
      if (typeof v === 'string') {
        const s = v.trim();
        if (!s) return null;
        if (s.toLowerCase() === 'null') return null;
        if (s.startsWith('{') || s.startsWith('[')) {
          try { return JSON.parse(s); } catch { return null; }
        }
      }
      return null;
    },
    openCurrentFile() {
      this.$emit('open-file', { url: this.currentFileUrl, fileName: this.currentFileName });
    },
    /**
     * 處理 DocumentPreviewPane（Feature 2）發出的 request-switch-column 事件。
     * 驗證目標欄位是否存在且有檔案，若合法則切換 activeField；否則忽略。
     */
    onDocumentPreviewRequestSwitchColumn({ column_name }) {
      const targetCol = (this.fileColumnsWithFile || []).find(
        c => c.flexfield_column === column_name
      );
      if (!targetCol) return;
      this.activeField = column_name;
    },
    /**
     * 供 Feature 1 呼叫：轉發 navigateTo 至 DocumentPreviewPane（Feature 2）。
     * 由 Task8739 在 Feature 1 接收到 Feature 3 的 on-citation-click 事件後呼叫。
     *
     * @param {Object} payload - { column_name, page_num, image_num }
     */
    callDocumentPreviewNavigateTo(payload) {
      this.$refs.documentPreviewPane?.navigateTo(payload);
    },
    /**
     * 取得 SAS URL 並寫入快取。
     * Feature 2 透過 inject 取得 sasUrlCache 後，可直接讀取快取；
     * SAS URL 的取得與寫入統一由此方法負責，避免重複請求。
     *
     * @param {string|number} rowObjectId - flexfielddata.object_id
     * @param {string} columnName - 欄位識別（flexfield_column）
     * @param {string} blobUrl - Azure Blob 路徑
     * @returns {Promise<string|null>} SAS URL 或 null
     */
    async fetchAndCacheSasUrl(rowObjectId, columnName, blobUrl) {
      const cacheKey = `${rowObjectId}:${columnName}`;
      // 快取命中：直接回傳，不重新請求
      if (this.sasUrlCache[cacheKey]) {
        return this.sasUrlCache[cacheKey];
      }
      // 快取未命中：請求 API 並寫入快取
      try {
        const res = await UpGPTPortalDataServices.getSasUrl(blobUrl);
        const sasUrl = res.data?.sas_url;
        if (sasUrl) {
          Vue.set(this.sasUrlCache, cacheKey, sasUrl);
        }
        return sasUrl || null;
      } catch (err) {
        console.error('[RRP_SAS_FETCH_ERROR]', err);
        return null;
      }
    },
    /**
     * 處理 RecordDataPane（Feature 3）emit 的 on-citation-click 事件。
     * 判斷目標欄位是否與左側目前顯示欄位相同，決定是否先切換欄位，
     * 再轉呼叫 DocumentPreviewPane（Feature 2）的 navigateTo 方法跳頁定位。
     *
     * 呼叫時機：由 DocumentAgentDetail（slot 提供者）監聽 Feature 3 的
     * on-citation-click 事件後，透過 $refs 呼叫此方法。
     *
     * @param {Object} payload - { column_name, page_num, image_num }
     * @param {string} payload.column_name - 目標欄位識別（flexfield_column）
     * @param {number|null} payload.page_num - 目標頁碼（PDF 適用）
     * @param {number|null} payload.image_num - 目標圖片序號（圖片適用）
     */
    handleCitationClick({ column_name, page_num = null, image_num = null }) {
      // 若目標欄位與左側目前顯示欄位不同，先切換欄位（驅動 props 傳遞至 Feature 2）
      if (column_name !== this.activeField) {
        this.activeField = column_name;
      }
      // 呼叫 Feature 2 的 navigateTo 跳頁定位
      this.callDocumentPreviewNavigateTo({ column_name, page_num, image_num });
    },
  },
}
</script>

<style scoped>
.record-review-overlay {
  position: absolute;
  inset: 0;
  z-index: 200;
  outline: none;
}

.record-review-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 0;
}

.record-review-header {
  height: 60px;
  flex: 0 0 auto;
  display: flex;
  border-bottom: 1px solid #CCCCCC;
}

.record-review-header-left {
  width: 50%;
  background-color: #FCFAF7;
}

.record-review-header-right {
  width: 50%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px;
}

/* breadcrumb + arrows 左側群組 */
.rr-left-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
}

.rr-breadcrumb-area {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
}

/* 上下箭頭垂直疊放容器 20×40 */
.rr-nav-frame {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 0 0 auto;
  width: 20px;
  height: 40px;
}

.rr-nav-btn {
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  color: #444444;
}

.rr-nav-btn:disabled {
  color: #999999;
  cursor: default;
}

.record-review-body {
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

.record-review-left-pane {
  height: 100%;
  overflow: auto;
  border-right: 1px solid #e0e0e0;
  background-color: #FCFAF7;
  box-shadow: inset 0px 8px 8px 0px #9999991A;
}

.record-review-right-pane {
  height: 100%;
  overflow: auto;
}

::v-deep .rr-bc-normal.v-breadcrumbs__item {
  color: #999999 !important;
  cursor: pointer;
}

::v-deep .rr-bc-active.v-breadcrumbs__item {
  color: #444444 !important;
  cursor: default;
}

/* 所有 divider 預設淡化 */
::v-deep .rr-breadcrumb-area .v-breadcrumbs__divider {
  color: #999999 !important;
}

/* 最後一個 divider（緊接在當前 table 前）顏色同當前 table */
::v-deep .rr-breadcrumb-area .v-breadcrumbs__divider:nth-last-child(2) {
  color: #444444 !important;
}

/* Left pane title bar */
.rrl-title-bar {
  display: flex;
  align-items: center;
  height: 60px;
  padding: 0 8px;
  gap: 4px;
  overflow: hidden;
}

.rrl-back-btn {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  border-radius: 4px;
}

.rrl-back-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.rrl-separator {
  flex: 0 0 auto;
  width: 1px;
  height: 24px;
  background-color: #CCCCCC;
}

.rrl-file-type-icon {
  flex: 0 0 auto;
}

.rrl-name-nav-group {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
}

.rrl-field-nav {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  width: 20px;
  height: 40px;
}


.rrl-filename {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #444444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
